import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users,
    Layers,
    ShieldHalf,
    Building2,
    Puzzle,
    TrendingUp,
    Activity,
    Clock,
    Plus,
    Search,
    MoreVertical,
    ArrowUpRight,
    ArrowDownRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Avatar, AvatarFallback } from '../components/ui/Avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import dashboardService from '../services/dashboard';
import Analytics from '../components/Analytics';
import Reports from '../components/Reports';

const StatCard = ({ title, value, change, icon, trend = 'up', description }) => {
    const TrendIcon = trend === 'up' ? ArrowUpRight : ArrowDownRight;
    const trendColor = trend === 'up' ? 'text-emerald-600' : 'text-red-600';

    return (
        <Card className="hover:shadow-lg transition-shadow duration-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
                <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                    {icon}
                </div>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-bold text-slate-900">{value}</div>
                {change !== undefined && !isNaN(change) && typeof change === 'number' && (
                    <div className={`flex items-center gap-1 text-sm mt-2 ${trendColor}`}>
                        <TrendIcon className="h-4 w-4" />
                        <span className="font-medium">{Math.abs(change)}%</span>
                        <span className="text-slate-500 ml-1">{description || 'vs last month'}</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const ActivityFeed = ({ activities }) => {
    return (
        <Card className="col-span-1 lg:col-span-2">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Recent Activity
                </CardTitle>
                <CardDescription>Latest system events and user actions</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {activities.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>
                    ) : (
                        activities.map((activity) => (
                            <div
                                key={activity.id}
                                className="flex items-start gap-4 pb-4 border-b border-slate-200 last:border-0"
                            >
                                <Avatar className="h-10 w-10">
                                    <AvatarFallback className="bg-primary-100 text-primary-600">
                                        {activity.user.charAt(0).toUpperCase()}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 space-y-1">
                                    <p className="text-sm">
                                        <span className="font-medium text-slate-900">{activity.user}</span>
                                        <span className="text-slate-600"> {activity.action} </span>
                                        <span className="font-medium text-slate-900">{activity.target}</span>
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                        <Clock className="h-3 w-3" />
                                        {activity.timestamp}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

const QuickActionsCard = ({ actions }) => {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>Common administrative tasks</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
                {actions.map((action) => (
                    <Button
                        key={action.id}
                        variant="outline"
                        className="h-auto py-4 flex flex-col items-center gap-2 hover:bg-primary-50 hover:border-primary-300"
                        onClick={action.onClick}
                    >
                        <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600">
                            {action.icon}
                        </div>
                        <span className="text-sm font-medium">{action.label}</span>
                    </Button>
                ))}
            </CardContent>
        </Card>
    );
};

const DashboardPage = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [stats, setStats] = useState({
        totalApps: 0,
        activeApps: 0,
        totalUsers: 0,
        totalRoles: 0,
        activeRoles: 0,
        totalTenants: 0,
        activeTenants: 0,
        totalComponents: 0,
        activeComponents: 0,
    });
    const [loading, setLoading] = useState(true);
    const [recentActivities, setRecentActivities] = useState([]);
    const [topApps, setTopApps] = useState([]);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            // Fetch dashboard overview data from the API
            const overview = await dashboardService.getDashboardOverview();

            if (overview) {
                const { statistics, topApps: topAppsData } = overview;

                // Helper function to safely convert to number
                const safeParseInt = (value) => {
                    if (value === null || value === undefined) return 0;
                    const num = typeof value === 'number' ? value : parseInt(value, 10);
                    return isNaN(num) ? 0 : num;
                };

                // Set statistics - ensure all values are numbers
                setStats({
                    totalApps: safeParseInt(statistics.total_apps),
                    activeApps: safeParseInt(statistics.active_apps),
                    totalUsers: safeParseInt(statistics.total_users),
                    totalRoles: safeParseInt(statistics.total_roles),
                    activeRoles: safeParseInt(statistics.active_roles),
                    totalTenants: safeParseInt(statistics.total_tenants),
                    activeTenants: safeParseInt(statistics.active_tenants),
                    totalComponents: safeParseInt(statistics.total_components),
                    activeComponents: safeParseInt(statistics.active_components),
                });

                // Transform top apps data for display
                const transformedTopApps = (topAppsData || []).map((app) => ({
                    name: app.app_name,
                    users: parseInt(app.user_count) || 0,
                    appId: app.app_id,
                    isActive: app.is_active === 1 || app.is_active === '1',
                }));

                setTopApps(transformedTopApps.slice(0, 3));

                // Generate mock recent activities (you can replace this with real activity data)
                setRecentActivities([
                    {
                        id: '1',
                        user: 'System',
                        action: 'loaded',
                        target: `${statistics.total_apps || 0} applications`,
                        timestamp: 'Just now',
                    },
                    {
                        id: '2',
                        user: 'Admin',
                        action: 'viewing dashboard',
                        target: '',
                        timestamp: 'Just now',
                    },
                ]);
            }
        } catch (err) {
            console.error('Error loading dashboard data:', err);
            // Set default values on error
            setStats({
                totalApps: 0,
                activeApps: 0,
                totalUsers: 0,
                totalRoles: 0,
                activeRoles: 0,
                totalTenants: 0,
                activeTenants: 0,
                totalComponents: 0,
                activeComponents: 0,
            });
            setTopApps([]);
        } finally {
            setLoading(false);
        }
    };

    const quickActions = [
        {
            id: '1',
            label: 'Add App',
            icon: <Layers className="h-4 w-4" />,
            onClick: () => navigate('/apps'),
        },
        {
            id: '2',
            label: 'Add User',
            icon: <Users className="h-4 w-4" />,
            onClick: () => navigate('/users'),
        },
        {
            id: '3',
            label: 'Create Role',
            icon: <ShieldHalf className="h-4 w-4" />,
            onClick: () => navigate('/roles'),
        },
        {
            id: '4',
            label: 'New Tenant',
            icon: <Building2 className="h-4 w-4" />,
            onClick: () => navigate('/tenants'),
        },
    ];

    // Calculate percentages for each stat
    const calculatePercentage = (active, total) => {
        const activeNum = typeof active === 'number' ? active : parseInt(active, 10) || 0;
        const totalNum = typeof total === 'number' ? total : parseInt(total, 10) || 0;
        if (!totalNum || totalNum === 0 || isNaN(totalNum)) return undefined;
        if (isNaN(activeNum)) return undefined;
        const percentage = Math.round((activeNum / totalNum) * 100);
        return isNaN(percentage) ? undefined : percentage;
    };

    const statCards = [
        {
            title: 'Total Apps',
            value: stats.totalApps,
            change: calculatePercentage(stats.activeApps, stats.totalApps),
            icon: <Layers className="h-4 w-4" />,
            trend: 'up',
            description: `${stats.activeApps || 0} active`,
        },
        {
            title: 'Active Users',
            value: typeof stats.totalUsers === 'number' ? stats.totalUsers.toLocaleString() : stats.totalUsers || 0,
            change: undefined, // No comparison data for users
            icon: <Users className="h-4 w-4" />,
            trend: 'up',
            description: 'across all apps',
        },
        {
            title: 'User Roles',
            value: stats.totalRoles,
            change: calculatePercentage(stats.activeRoles, stats.totalRoles),
            icon: <ShieldHalf className="h-4 w-4" />,
            trend: 'up',
            description: `${stats.activeRoles || 0} active`,
        },
        {
            title: 'Tenants',
            value: stats.totalTenants,
            change: calculatePercentage(stats.activeTenants, stats.totalTenants),
            icon: <Building2 className="h-4 w-4" />,
            trend: 'up',
            description: `${stats.activeTenants || 0} active`,
        },
        {
            title: 'Components',
            value: stats.totalComponents,
            change: calculatePercentage(stats.activeComponents, stats.totalComponents),
            icon: <Puzzle className="h-4 w-4" />,
            trend: 'up',
            description: `${stats.activeComponents || 0} active`,
        },
    ];

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="h-8 bg-slate-200 rounded w-64 animate-pulse" />
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Card key={i}>
                            <CardContent className="p-6">
                                <div className="h-20 bg-slate-200 rounded animate-pulse" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
                    <p className="text-slate-600 mt-1">Overview of your user management system</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 md:flex-initial">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 w-full md:w-64"
                        />
                    </div>
                    <Button className="gap-2" onClick={() => navigate('/apps')}>
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add App</span>
                    </Button>
                </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {statCards.map((stat, index) => (
                    <StatCard key={index} {...stat} />
                ))}
            </div>

            <Tabs defaultValue="overview" className="space-y-6">
                <TabsList className="grid w-full max-w-md grid-cols-3">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="analytics">Analytics</TabsTrigger>
                    <TabsTrigger value="reports">Reports</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                    <div className="grid gap-6 lg:grid-cols-3">
                        <ActivityFeed activities={recentActivities} />
                        <QuickActionsCard actions={quickActions} />
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <span>Top Applications</span>
                                    <MoreVertical className="h-4 w-4 text-slate-400" />
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {topApps.length === 0 ? (
                                        <p className="text-sm text-slate-500 text-center py-4">No applications yet</p>
                                    ) : (
                                        topApps.map((app, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center justify-between cursor-pointer hover:bg-slate-50 p-2 rounded-lg -m-2"
                                                onClick={() =>
                                                    navigate(`/users?app_id=${encodeURIComponent(app.appId)}`)
                                                }
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                                                        <Layers className="h-5 w-5 text-primary-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-sm text-slate-900">{app.name}</p>
                                                        <p className="text-xs text-slate-500">{app.users} users</p>
                                                    </div>
                                                </div>
                                                <Badge variant={app.isActive ? 'default' : 'secondary'}>
                                                    {app.isActive ? (
                                                        <TrendingUp className="h-3 w-3" />
                                                    ) : (
                                                        <ArrowDownRight className="h-3 w-3" />
                                                    )}
                                                </Badge>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <span>System Status</span>
                                    <Badge variant="default" className="bg-emerald-500">
                                        Healthy
                                    </Badge>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600">Applications</span>
                                            <span className="font-medium text-slate-900">{stats.totalApps} active</span>
                                        </div>
                                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-emerald-500"
                                                style={{ width: `${Math.min((stats.totalApps / 10) * 100, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600">Users</span>
                                            <span className="font-medium text-slate-900">{stats.totalUsers} total</span>
                                        </div>
                                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-primary-600"
                                                style={{ width: `${Math.min((stats.totalUsers / 1000) * 100, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-600">Components</span>
                                            <span className="font-medium text-slate-900">
                                                {stats.totalComponents} registered
                                            </span>
                                        </div>
                                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-blue-500"
                                                style={{
                                                    width: `${Math.min((stats.totalComponents / 200) * 100, 100)}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Quick Links</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {[
                                        { label: 'All Applications', path: '/apps', icon: Layers },
                                        { label: 'Manage Users', path: '/users', icon: Users },
                                        { label: 'Roles & Permissions', path: '/roles', icon: ShieldHalf },
                                        { label: 'Components', path: '/components', icon: Puzzle },
                                        { label: 'Tenants', path: '/tenants', icon: Building2 },
                                    ].map((link, i) => (
                                        <button
                                            key={i}
                                            onClick={() => navigate(link.path)}
                                            className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                                        >
                                            <div className="h-8 w-8 rounded-lg bg-primary-100 flex items-center justify-center">
                                                <link.icon className="h-4 w-4 text-primary-600" />
                                            </div>
                                            <span className="font-medium text-sm text-slate-900">{link.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="analytics">
                    <Analytics />
                </TabsContent>

                <TabsContent value="reports">
                    <Reports />
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default DashboardPage;
